// ============================================================================
// CHECK-IN SERVICE
// ============================================================================

import { get, post, put } from '../client/axiosClient';
import { checkInEndpoints } from '../endpoints/checkin.endpoints';
import { buildEndpointWithQuery, buildEndpointWithParams } from '../endpoints/base.endpoints';
import type { ApiResponse, ID } from '../types';
import type { Booking } from '../types/booking.types';
import type {
  AmendCheckInRequest,
  ArrivalChangeRequest,
  AvailableRoom,
  CheckIn,
  CheckInInventoryImpact,
  MarkNoShowRequest,
  MarkNoShowRoomsRequest,
  QuickCheckInRequest,
} from '../types/checkin.types';

class CheckInService {
  async checkIn(bookingId: ID, payload: QuickCheckInRequest): Promise<CheckIn> {
    const response = await post<ApiResponse<CheckIn>>(
      checkInEndpoints.checkIn(bookingId),
      payload,
    );
    if (!response.data?.data) throw new Error(response.data?.message || 'Check-in failed.');
    return response.data.data;
  }

  /**
   * Amends an existing check-in while the booking is CHECKED_IN — arrival
   * date/time, occupancy, guest rows, GRC details. Rooms and money are out
   * of scope by contract.
   */
  async amend(bookingId: ID, payload: AmendCheckInRequest): Promise<CheckIn> {
    const response = await put<ApiResponse<CheckIn>>(
      checkInEndpoints.amend(bookingId),
      payload,
    );
    if (!response.data?.data) throw new Error(response.data?.message || 'Check-in update failed.');
    return response.data.data;
  }

/**
   * Applies front-desk arrival changes (pax / meal plan / extra bed / agreed
   * rate) to a CONFIRMED booking. Requires the 'arrival-changes' permission —
   * NOT booking edit. Returns the re-priced booking; when the change is
   * materially worse the booking comes back PENDING_APPROVAL and check-in waits.
   */
  async applyArrivalChanges(bookingId: ID, payload: ArrivalChangeRequest): Promise<Booking> {
    const response = await post<ApiResponse<Booking>>(
      buildEndpointWithParams(`/CheckIn/:bookingId/arrival-changes`, { bookingId }),
      payload,
    );
    if (!response.data?.data) throw new Error(response.data?.message || 'Arrival changes failed.');
    return response.data.data;
  }

  /**
   * Raises a request-only FO change request (FO_CHANGE_REQUEST approval) —
   * no change is applied to the booking. Allowed on CONFIRMED / CHECKED_IN.
   */
  async raiseChangeRequest(bookingId: ID, requestText: string,
    requestedAmount?: number | null, requestedBasis?: 'PER_NIGHT' | 'STAY_TOTAL' | null): Promise<void> {
    const response = await post<ApiResponse<unknown>>(
      checkInEndpoints.changeRequest(bookingId),
      { requestText, requestedAmount: requestedAmount ?? undefined, requestedBasis: requestedAmount != null ? requestedBasis : undefined },
    );
    if (response.data?.success === false) {
      throw new Error(response.data.message || 'Failed to send the request.');
    }
  }

  /**
   * Marks N rooms of a partially-arrived in-house booking as no-show —
   * releases their remaining nights and routes the charging decision to
   * booking staff (NO_SHOW_PRICING work item).
   */
  async markNoShowRooms(bookingId: ID, payload: MarkNoShowRoomsRequest): Promise<void> {
    const response = await post<ApiResponse<unknown>>(
      checkInEndpoints.markNoShowRooms(bookingId),
      payload,
    );
    if (response.data?.success === false) {
      throw new Error(response.data.message || 'Failed to mark rooms no-show.');
    }
  }

  async markNoShow(bookingId: ID, payload: MarkNoShowRequest): Promise<void> {
    const response = await post<ApiResponse<unknown>>(
      checkInEndpoints.markNoShow(bookingId),
      payload,
    );
    if (response.data?.success === false) {
      throw new Error(response.data.message || 'Failed to mark as No-Show.');
    }
  }

  async getByBooking(bookingId: ID): Promise<CheckIn | null> {
    try {
      const response = await get<ApiResponse<CheckIn>>(checkInEndpoints.getByBooking(bookingId));
      return response.data?.data ?? null;
    } catch (err: any) {
      // 404 is expected when the booking hasn't been checked in yet.
      if (err?.response?.status === 404) return null;
      throw err;
    }
  }

  /** forCorrection (Audit Corrections): rooms in house today are listed too — a past stay is checked against its own nights on save. */
  async getAvailableRooms(
    propertyId: ID,
    roomCategoryId: ID,
    checkInDate: string,
    forCorrection = false,
  ): Promise<AvailableRoom[]> {
    const url = buildEndpointWithQuery('/CheckIn/available-rooms', {
      propertyId,
      roomCategoryId,
      checkInDate,
      forCorrection: forCorrection || undefined,
    });
    const response = await get<ApiResponse<AvailableRoom[]>>(url);
    return response.data?.data ?? [];
  }

  /**
   * Nightly inventory position per category across the booking's stay, so the
   * shortfall can be shown while the desk is still choosing a category.
   *
   * Advisory only — the server re-derives the same numbers at save time and
   * refuses an unacknowledged shortage, so a failure here degrades the warning
   * without ever letting exposure through silently.
   */
  async getInventoryImpact(bookingId: ID): Promise<CheckInInventoryImpact | null> {
    const response = await get<ApiResponse<CheckInInventoryImpact>>(
      checkInEndpoints.inventoryImpact(bookingId),
    );
    return response.data?.data ?? null;
  }
}

export const checkInService = new CheckInService();
export default checkInService;
